import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useEffect } from 'react';
import {
    PaymentElement,
    useStripe,
    useElements,
    Elements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { useMutation } from '@apollo/client';
import { CREATE_PAYMENT_INTENT } from '../../gql-operations/mutations/purchase/create-payment-intent';
import environment from '../../env-config';

const IS_PRERENDER =
    typeof navigator !== 'undefined' &&
    (
        navigator.userAgent === 'ReactSnap' ||
        navigator.userAgent.includes('ReactSnap') ||
        (typeof window !== 'undefined' && typeof window.snapSaveState === 'function')
    );
let stripePromise = null;
let stripePromiseInitialized = false;

const getStripePromise = () => {
    if (IS_PRERENDER || !environment.REACT_STRIPE_PUBLISHABLE_KEY) {
        return null;
    }

    if (!stripePromiseInitialized) {
        stripePromiseInitialized = true;
        stripePromise = loadStripe(environment.REACT_STRIPE_PUBLISHABLE_KEY).catch(error => {
            console.warn('Failed to initialize Stripe payment form:', error);
            return null;
        });
    }

    return stripePromise;
};

export const StripeProvider = ({ children }) => {
    const stripePromise = getStripePromise();

    if (!stripePromise) {
        return <>{children}</>;
    }

    return <Elements stripe={stripePromise}>{children}</Elements>;
};

const DEFAULT_APPEARANCE = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#0570de',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'Ideal Sans, system-ui, sans-serif',
        spacingUnit: '2px',
        borderRadius: '4px',
    }
};

const DEFAULT_PAYMENT_ELEMENT_OPTIONS = {
    layout: 'tabs',
    paymentMethodOrder: ['card', 'ideal', 'bancontact', 'eps'],
};

const PaymentForm = ({
    onSuccess,
    onError,
    appearance,
    returnUrl,
    submitLabel = 'Pay now',
    paymentElementOptions = DEFAULT_PAYMENT_ELEMENT_OPTIONS
}) => {
    const {t: lnT} = useTranslation();

    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);

        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: returnUrl || `${window.location.origin}/payment-success`,
            },
            redirect: 'if_required'
        });

        if (result.error) {
            onError?.(result.error);
            setIsProcessing(false);
            return;
        }

        onSuccess?.(result.paymentIntent || result);
        setIsProcessing(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={paymentElementOptions} />
            <button
                disabled={isProcessing || !stripe || !elements}
                style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '20px',
                    backgroundColor: appearance?.variables?.colorPrimary || '#0570de',
                    border: 'none',
                    borderRadius: appearance?.variables?.borderRadius || '4px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                }}
            >
                {isProcessing ? lnT('messages:processing') : submitLabel}
            </button>
        </form>
    );
};

export const StripeElementsShell = ({
    clientSecret,
    appearance,
    onSuccess,
    onError,
    returnUrl,
    submitLabel,
    paymentElementOptions,
    loadingLabel = 'Loading payment form...'
}) => {
    const {t: lnT} = useTranslation();

    const stripePromise = getStripePromise();

    if (!stripePromise) {
        return (
            <div className="loading" style={{ 
                color: appearance?.variables?.colorText || '#30313d',
                textAlign: 'center',
                padding: '20px'
            }}>{lnT('common:stripeIsNotConfiguredPleaseCheckYour')}</div>
        );
    }

    if (!clientSecret) {
        return (
            <div className="loading" style={{ 
                color: appearance?.variables?.colorText || '#30313d',
                textAlign: 'center',
                padding: '20px'
            }}>
                {loadingLabel}
            </div>
        );
    }

    return (
        <Elements 
            stripe={stripePromise} 
            options={{ 
                clientSecret,
                appearance: appearance || DEFAULT_APPEARANCE
            }}
        >
            <PaymentForm
                onSuccess={onSuccess}
                onError={onError}
                appearance={appearance}
                returnUrl={returnUrl}
                submitLabel={submitLabel}
                paymentElementOptions={paymentElementOptions}
            />
        </Elements>
    );
};

export const PaymentFormWrapper = (props) => {
    const {t: lnT} = useTranslation();

    const [clientSecret, setClientSecret] = useState();
    const [error, setError] = useState();

    const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT, {
        onCompleted: (data) => {
            if (data.createPaymentIntent.__typename === 'PaymentIntent') {
                setClientSecret(data.createPaymentIntent.clientSecret);
            } else {
                setError(data.createPaymentIntent.message);
                if (props.onError) {
                    props.onError(new Error(data.createPaymentIntent.message));
                }
            }
        },
        onError: (error) => {
            console.error('GraphQL Error:', error);
            const errorMessage = error.graphQLErrors?.[0]?.message || error.message;
            setError(errorMessage);
            if (props.onError) {
                props.onError(new Error(errorMessage));
            }
        }
    });

    useEffect(() => {
        createPaymentIntent({
            variables: {
                data: {
                    userId: props.userId,
                    planId: props.planId,
                    quantity: props.quantity || 1,
                    coupon: props.coupon
                }
            }
        });
    }, [props.userId, props.planId, props.quantity, props.coupon]);

    if (error) {
        return (
            <div className="error-message" style={{ 
                color: props.appearance?.variables?.colorDanger || '#df1b41', 
                textAlign: 'center', 
                padding: '20px' 
            }}>
                <p>{lnT('common:error_8eecdde0')}{error}</p>
                <p>{lnT('common:pleaseTryAgainLaterOrContactSupport')}</p>
            </div>
        );
    }

    return (
        <StripeElementsShell
            clientSecret={clientSecret}
            appearance={props.appearance}
            onSuccess={props.onSuccess}
            onError={props.onError}
            returnUrl={props.returnUrl}
            submitLabel={props.submitLabel}
            paymentElementOptions={props.paymentElementOptions}
        />
    );
};

export default PaymentFormWrapper; 
